import { useState, useEffect, useCallback } from 'react';
import { RisksSection } from './RisksSection';
import { IssuesSection } from './IssuesSection';
import { AssumptionsSection } from './AssumptionsSection';
import { DependenciesSection } from './DependenciesSection';
import { HeroSection } from '../HeroSection';
import { AlertTriangle, AlertCircle, Lightbulb, Link, Sparkles } from 'lucide-react';
import { MeetingNotesDialog } from './MeetingNotesDialog';
import { projectRaidService } from '../../services/projectRaid';

interface ProjectRAIDSectionProps {
  projectId?: number;
}

export function ProjectRAIDSection({ projectId }: ProjectRAIDSectionProps) {
  const [activeSubTab, setActiveSubTab] = useState('risks');
  const [counts, setCounts] = useState({ risks: 0, issues: 0, assumptions: 0, dependencies: 0 });
  const [notesOpen, setNotesOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const refreshCounts = useCallback(() => {
    if (!projectId) {
      setCounts({ risks: 0, issues: 0, assumptions: 0, dependencies: 0 });
      return;
    }
    Promise.all([
      projectRaidService.list(projectId, 'risk'),
      projectRaidService.list(projectId, 'issue'),
      projectRaidService.list(projectId, 'assumption'),
      projectRaidService.list(projectId, 'dependency'),
    ]).then(([risks, issues, assumptions, dependencies]) => {
      setCounts({
        risks: risks.length,
        issues: issues.length,
        assumptions: assumptions.length,
        dependencies: dependencies.length,
      });
    }).catch((err) => {
      // RAID summary counts are best-effort — failure just leaves the
      // last good count visible. Log so dev tools surface it.
      console.warn('Failed to refresh RAID counts:', err)
    });
  }, [projectId]);

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  return (
    <div>
      <div className="p-6 pb-0">
        <HeroSection
          title="RAID Log"
          description="Manage Risks, Assumptions, Issues, and Dependencies"
          variant="level4"
          metrics={[
            { label: 'Active Risks', value: String(counts.risks), icon: AlertTriangle, iconColor: 'bg-red-50' },
            { label: 'Open Issues', value: String(counts.issues), icon: AlertCircle, iconColor: 'bg-orange-50' },
            { label: 'Assumptions', value: String(counts.assumptions), icon: Lightbulb, iconColor: 'bg-blue-50' },
            { label: 'Dependencies', value: String(counts.dependencies), icon: Link, iconColor: 'bg-purple-50' },
          ]}
        />
      </div>

      {projectId && (
        <div className="flex justify-end px-6 pt-4">
          <button
            type="button"
            onClick={() => setNotesOpen(true)}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-md border border-border bg-card px-3 text-sm font-medium text-foreground hover:bg-muted"
          >
            <Sparkles className="h-4 w-4 text-primary" /> From meeting notes
          </button>
        </div>
      )}
      {notesOpen && projectId && (
        <MeetingNotesDialog
          projectId={projectId}
          onClose={() => setNotesOpen(false)}
          onAdded={() => { setReloadKey((k) => k + 1); refreshCounts(); }}
        />
      )}

      <div className="bg-white border-b border-gray-200">
        <div className="flex gap-6 px-6">
          <button
            onClick={() => setActiveSubTab('risks')}
            className={`pb-3 px-1 border-b-2 transition-colors ${
              activeSubTab === 'risks'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Risks
          </button>
          <button
            onClick={() => setActiveSubTab('assumptions')}
            className={`pb-3 px-1 border-b-2 transition-colors ${
              activeSubTab === 'assumptions'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Assumptions
          </button>
          <button
            onClick={() => setActiveSubTab('issues')}
            className={`pb-3 px-1 border-b-2 transition-colors ${
              activeSubTab === 'issues'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Issues
          </button>
          <button
            onClick={() => setActiveSubTab('dependencies')}
            className={`pb-3 px-1 border-b-2 transition-colors ${
              activeSubTab === 'dependencies'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Dependencies
          </button>
        </div>
      </div>

      {activeSubTab === 'risks' && <RisksSection key={reloadKey} projectId={projectId} onChanged={refreshCounts} />}
      {activeSubTab === 'assumptions' && <AssumptionsSection key={reloadKey} projectId={projectId} onChanged={refreshCounts} />}
      {activeSubTab === 'issues' && <IssuesSection key={reloadKey} projectId={projectId} onChanged={refreshCounts} />}
      {activeSubTab === 'dependencies' && <DependenciesSection key={reloadKey} projectId={projectId} onChanged={refreshCounts} />}
    </div>
  );
}
